// Used in singular-assignment-container.component.ts, lti-iframe.component.ts, math-ext-link-activity.component.ts, workspace-details.component.ts,
// assignable-preview-details.component.ts, lesson-view.component.ts, student-assignments.component.ts

// Angular
import { Inject, Injectable } from "@angular/core";

// Services/Types/Helpers
import { Asset, Bundle, CogTutorWorkspace, CustomActivity, ExtLink, assetActivityTypeIds, assetTypeIds } from "@carnegie-learning/cure-types";
import {
    CompositeTaskAssignment,
    PPAssignment,
    PPUser as User,
} from "cli-types";
import { TimezoneHelper } from "./timezone.helper";

@Injectable({ providedIn: "root" })
export class AssignmentHelper {
    readonly assetActivityTypeIds = assetActivityTypeIds;
    private exceptions = {
        [assetTypeIds.COG_TUTOR]: [
            this.assetActivityTypeIds.MASTERY,
            this.assetActivityTypeIds.NON_MASTERY,
        ],
    };
    constructor(
        @Inject(TimezoneHelper) private timezoneHelper: TimezoneHelper
    ) {}

    /**
     * @method convertAssignmentTypeToTypeTitle
     * @description converts assignmentType to UI assignment display type
     * @param {string} assignmentType
     * @returns {string}
     */
    convertAssignmentTypeToTypeTitle(assignmentType: string): string {
        let assignmentTypeTitle = "";

        switch (assignmentType) {
            case assetTypeIds.PERFORM:
                assignmentTypeTitle = "Performance";
                break;
            case "Module":
                assignmentTypeTitle = "Assessment";
                break;
            case assetTypeIds.EBOOK_ACT:
                assignmentTypeTitle = "Discover";
                break;
            case "Assessment":
                assignmentTypeTitle = "Assess";
                break;
            case assetTypeIds.EREADER:
                assignmentTypeTitle = "Resource Activity";
                break;
            case assetTypeIds.CUSTOM_ACT:
                assignmentTypeTitle = "Journal Activity";
                break;
            case assetTypeIds.READYCHECK:
                assignmentTypeTitle = "ReadyCheck";
                break;
        }
        return assignmentTypeTitle;
    }

    convertAssignmentToUserTimezone<
        T extends PPAssignment | CompositeTaskAssignment
    >(assignment: T, userProfile?: User): T {
        if (userProfile?.timezone) {
            return this.timezoneHelper.updateObjServerTZToUserTZ(
                assignment,
                userProfile.timezone
            );
        }

        return assignment;
    }

    isCustomSpanishBundle(assignment: PPAssignment): boolean {
        const asset = assignment.asset;
        if (asset?.publishedBy && assignment.locale) {
            return (
                asset.publishedBy !== "CL" &&
                assignment.locale === "es_PR" &&
                asset.assetTypeId === assetTypeIds.BUNDLE &&
                asset.subjectId === "MATH"
            );
        }
        return false;
    }

    getSpanishTaskTitle(taskAssetId: string, assignment: PPAssignment): string {
        let title = "---";
        if (assignment?.asset) {
            const foundActivity = (<Bundle>assignment.asset).tasks?.find(
                (t) => t.assetId === taskAssetId
            );
            if (foundActivity) {
                if (
                    assignment.locale === "es_PR" &&
                    foundActivity.linkedAssets
                ) {
                    title =
                        foundActivity?.linkedAssets.find((a) => a.locales)
                            ?.displayName || "---";
                } else {
                    title = foundActivity.displayName;
                }
            }
        }
        return title;
    }

    // Replaces extension function from es-mime-types as it is Node.js module and not
    // available in a browser environment
    getFileExtension(filename: string | undefined){
        return filename?.split('.').pop() || "";
    }

    getFileType(asset: Asset | undefined): string {
        const contentType = (<ExtLink>asset)?.contentType;
        if (contentType) {
            const fileExt = this.getFileExtension(contentType);
            return fileExt ? fileExt.toUpperCase() : "File";
        }

        return "File";
    }

    hasLaunchLink(asset: Asset | undefined): boolean {
        return (
            asset?.assetTypeId === assetTypeIds.EXT_LINK &&
            !!(<ExtLink>asset).launchLink
        );
    }

    public ifIframeAssignment(type: string): boolean {
        return (
            type === assetTypeIds.EBOOK_ACT ||
            type === assetTypeIds.EREADER ||
            type === this.assetActivityTypeIds.IPA ||
            type === assetTypeIds.COG_TUTOR ||
            type === assetTypeIds.READYCHECK ||
            type === assetTypeIds.C2C
        );
    }
    // check if resource activity
    ifResourceActivityType(shortAssignableType: string) {
        return shortAssignableType.toLowerCase() === "e-reader";
    }
    ifMathActivityType(shortAssignableType: string) {
        return (
            shortAssignableType.toLowerCase() === "mathconceptbuilder" ||
            shortAssignableType.toLowerCase() === "mathmastery" ||
            shortAssignableType.toLowerCase() === "mastery" ||
            shortAssignableType.toLowerCase() === "non-mastery" ||
            shortAssignableType.toLowerCase() === "cog-tutor"
        );
    }
    // Format Custom Activity's Response Type
    formatResponseType(responseType: string) {
        if (responseType === "both") {
            return "Short Answer and/or Upload";
        } else if (responseType === "text") {
            return "Short Answer";
        } else if (responseType === "file") {
            return "Upload";
        } else if (responseType === "none") {
            return "Mark as Submitted";
        }
        throw new Error(`unknown response type: ${responseType}`);
    }
    // Teacher - If a Teacher has reset a student's assignment back to to-do/pending, still show the attempt
    hasSubmission(assignment: PPAssignment | CompositeTaskAssignment) {
        let sat: string | undefined;
        if (
            assignment &&
            (<CompositeTaskAssignment>assignment).compositeTaskAssignmentID
        ) {
            sat = (<CompositeTaskAssignment>assignment).assetTypeId;
        } else {
            sat = (<PPAssignment>assignment).shortAssignableType;
        }

        if (sat === assetTypeIds.PERFORM) {
            // Performance/Avenue
            if (assignment.studentVideoUrl) {
                return true;
            }
            
        } else if (sat === assetTypeIds.EBOOK_ACT) {
            // Ebook
            if (
                assignment.gradeAttempts &&
                assignment.gradeAttempts.length > 0
            ) {
                return true;
            }
        } else if (sat === this.assetActivityTypeIds.IPA) {
            // IPAs
            return this.compositeHasSubmission(assignment);
        } else if (sat === assetTypeIds.CUSTOM_ACT) {
            // Custom Activities
            let responseType: string;
            if (assignment.asset) {
                responseType = (<CustomActivity>assignment.asset).responseType;
                if (responseType === "none") {
                    return false;
                } else if (responseType === "text") {
                    if (assignment.submissionText) {
                        return true;
                    }
                } else if (responseType === "file") {
                    if (
                        assignment.submissionFilePath ||
                        assignment.submissionUrl
                    ) {
                        return true;
                    }
                } else if (responseType === "both") {
                    if (
                        assignment.submissionFilePath ||
                        assignment.submissionUrl ||
                        assignment.submissionText
                    ) {
                        return true;
                    }
                }
            }
        } else if (sat === assetTypeIds.BUNDLE) {
            // Bundles
            return this.compositeHasSubmission(assignment);
        } else if (sat === assetTypeIds.EREADER) {
            // No submission necessary for EREADER
        } else {
            return (
                (<PPAssignment>assignment).status === "submitted" ||
                (<PPAssignment>assignment).status === "evaluated"
            );
        }

        return false;
    }
    // Check each BundleIPA's activity for their unique submission status, using our already built
    // hasSubmission function.
    compositeHasSubmission(assignment: PPAssignment | CompositeTaskAssignment) {
        let sat: string | undefined;
        if (
            assignment &&
            (<CompositeTaskAssignment>assignment).compositeTaskAssignmentID
        ) {
            sat = (<CompositeTaskAssignment>assignment).assetTypeId;
        } else {
            sat = (<PPAssignment>assignment).shortAssignableType;
        }
        if (
            sat === this.assetActivityTypeIds.IPA ||
            sat === assetTypeIds.BUNDLE
        ) {
            const activities = (<PPAssignment>assignment)
                .compositeTaskAssignments;
            let containsSubmitted: boolean | undefined = false;
            activities.forEach((a) => {
                if (this.hasSubmission(a)) {
                    containsSubmitted = true;
                }
            });
            return containsSubmitted;
        }

        return false;
    }
    getAssetProgressPossible(
        asset: CogTutorWorkspace | Bundle
    ): string | number {
        if (asset) {
            let assetProgressPossible = (<CogTutorWorkspace>asset)
                .progressPossible;
            if (assetProgressPossible) {
                if (assetProgressPossible.toString().indexOf(".") > -1) {
                    assetProgressPossible = parseFloat(
                        assetProgressPossible.toFixed(1)
                    ); // only apply toFixed if the number is a decimal
                }
                return assetProgressPossible;
            } else if ((<Bundle>asset).assetTypeId === assetTypeIds.BUNDLE) {
                let progressPossible = 0;
                (<Bundle>asset).tasks?.forEach((task) => {
                    const taskProgressPossible = (<CogTutorWorkspace>task)
                        .progressPossible;
                    if (
                        task.assetTypeId === assetTypeIds.COG_TUTOR &&
                        taskProgressPossible
                    ) {
                        progressPossible += taskProgressPossible;
                    }
                });
                if (progressPossible > 0) {
                    return progressPossible.toFixed(1); // round to nearest decimal.
                }
            }
        }
        return "N/A";
    }
    // assetActivityTypeIds exception
    checkAssignableTypeException(
        assignableType: string[],
        replaceValue = false
    ): string[] {
        Object.keys(this.exceptions).forEach((key) => {
            if (assignableType?.includes(key)) {
                assignableType = assignableType.filter((each) => each !== key);
                if (replaceValue) {
                    assignableType = [
                        ...assignableType,
                        ...(<any>this.exceptions)[key],
                    ];
                }
            }
        });
        return assignableType;
    }
    // PAS-9381: Tasks should be represented in the order defined by the Bundle Asset
    sortCompositeTasksByAsset(
        compositeTaskAssignments: CompositeTaskAssignment[],
        asset: Bundle
    ): CompositeTaskAssignment[] {
        if (asset && compositeTaskAssignments) {
            const sortedCompositeTaskAssignments =
                compositeTaskAssignments.slice();
            return sortedCompositeTaskAssignments.sort(
                (a, b) =>
                    asset.tasks.findIndex((t) => a.assetId === t.assetId) -
                    asset.tasks.findIndex((t) => b.assetId === t.assetId)
            );
        }
        return compositeTaskAssignments;
    }
    // Get correct asset name
    getAssetDisplayName(asset: Asset): string {
        let name = "";
        if (asset) {
            name = asset.displayName;
            // If bundle is part of an IPA, show the IPA's name
            if (asset.assetActivityTypeId === this.assetActivityTypeIds.IPA) {
                name =
                    (<Bundle>asset).ipaName +
                    " - Part: " +
                    (<Bundle>asset).categoryOrder +
                    ": " +
                    asset.displayName;
            }
        }
        return name;
    }
}
